import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Film, Users, BarChart3, Settings, Plus, Trash2, Edit, Eye,
  TrendingUp, Layers, Upload, X, Save, Search, Play, Tv,
  UserX, UserCheck, KeyRound, ChevronLeft, ChevronRight,
  Tag, Globe, Star, CreditCard, Check, Ticket, Calendar, Percent,
  Volume2, Joystick, ChevronUp, ChevronDown, RefreshCw, Sparkles, SlidersHorizontal, AlertTriangle, Scissors, GripVertical
} from 'lucide-react';
import { TrimEditor, fmtTrim } from './TrimEditor';
import { AiTrailerButton, AiTrailerBulkButton } from './AiTrailer';
import { ConfirmModal } from './TrashTab';
import FilterTimelineEditor from '../FilterTimelineEditor';
import { LanguageStats } from '../analytics/LanguageStats';
import {
  API, CATEGORIES_LIST, ALL_COUNTRIES, MEDIA_SPECS, LANGUAGES, TYPE_LABELS, TYPE_LABELS_PLURAL, uploadFileToS3, FilterBadge,
  formatFileSize, TimecodeInput, CountryPicker, FilterSelect, LoadingSpinner as SharedLoadingSpinner, validateMediaFile
} from './shared';
import { EpisodePreview, EpisodePreviewModal } from './EpisodePreview';
import { LoadingSpinner, MissingMediaBadge } from './core';
import { ContentForm } from './ContentForm';

export function ContentsTab({ filterType: fixedType, theme }) {
  const light = theme === 'light';
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editContent, setEditContent] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState(fixedType || '');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('views');
  const [ratingStats, setRatingStats] = useState({});

  useEffect(() => {
    axios.get(`${API}/admin/ratings-stats`, { withCredentials: true }).then(r => setRatingStats(r.data || {})).catch(() => {});
  }, []);

  const fetch = useCallback(async () => {
    try {
      const p = { include_drafts: true }; if (search) p.search = search;
      if (filterType === 'originals') p.category = 'originals'; else if (filterType) p.type = filterType;
      const { data } = await axios.get(`${API}/contents`, { params: p, withCredentials: true });
      setContents(data);
    } catch {} finally { setLoading(false); }
  }, [search, filterType]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async (form) => {
    try {
      if (editContent) await axios.put(`${API}/contents/${editContent.id}`, form, { withCredentials: true });
      else if (form.id) await axios.put(`${API}/contents/${form.id}`, form, { withCredentials: true });
      else await axios.post(`${API}/contents`, form, { withCredentials: true });
      setShowForm(false); setEditContent(null); fetch();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const [confirmDelete, setConfirmDelete] = useState(null);
  const handleDelete = (id) => setConfirmDelete(contents.find(c => c.id === id) || { id });
  const doDelete = async () => { const cid = confirmDelete.id; setConfirmDelete(null); await axios.delete(`${API}/contents/${cid}`, { withCredentials: true }); fetch(); };

  const label = TYPE_LABELS_PLURAL[fixedType] || 'Tous les contenus';

  const likeScore = (c) => ratingStats[c.id]?.score || 0;
  const sortedContents = [...contents].sort((a, b) => {
    if (sortBy === 'title') return (a.title || '').localeCompare(b.title || '', 'fr');
    if (sortBy === 'created_at') return (b.created_at || '').localeCompare(a.created_at || '');
    if (sortBy === 'liked') return likeScore(b) - likeScore(a) || (b.views || 0) - (a.views || 0);
    return (b.views || 0) - (a.views || 0);
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h1 className={`text-2xl font-bold ${light ? 'text-gray-900' : 'text-white'}`}>{label} ({contents.length})</h1>
        {confirmDelete && <ConfirmModal light={light} title="Mettre à la corbeille ?"
          message={`« ${confirmDelete.title || 'Ce contenu'} » sera retiré de la vue des utilisateurs et placé dans la corbeille. Vous pourrez le restaurer ou le supprimer définitivement depuis l'onglet Corbeille.`}
          confirmLabel="Mettre à la corbeille" onConfirm={doDelete} onCancel={() => setConfirmDelete(null)} />}
        {!showForm && <div className="flex items-center gap-2">
          <AiTrailerBulkButton light={light} />
          <button onClick={() => { setEditContent(null); setShowForm(true); }} className="flex items-center gap-1.5 bg-[#E9641A] hover:bg-[#FE975E] text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"><Plus size={14} /> Ajouter</button>
        </div>}
      </div>
      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 relative"><Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${light ? 'text-gray-400' : 'text-white/25'}`} /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className={`w-full rounded-lg pl-9 pr-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${light ? 'bg-white border border-gray-200 text-gray-900 placeholder-gray-400' : 'bg-white/5 border border-white/8 text-white'}`} /></div>
        {!fixedType && <select value={filterType} onChange={e => setFilterType(e.target.value)} className={`rounded-lg px-3 py-2 text-xs ${light ? 'bg-white border border-gray-200 text-gray-700' : 'bg-white/5 border border-white/8 text-white'}`}><option value="">Tous</option><option value="fiction">Fictions</option><option value="series">Séries</option><option value="documentaire">Documentaires</option><option value="jeunesse">Jeunesse</option><option value="game">Jeux</option><option value="originals">Originals Maya+</option></select>}
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} data-testid="contents-sort-select" className={`rounded-lg px-3 py-2 text-xs ${light ? 'bg-white border border-gray-200 text-gray-700' : 'bg-white/5 border border-white/8 text-white'}`}>
          <option value="views">Les plus vus</option>
          <option value="liked">Les plus aimés</option>
          <option value="title">Nom (A-Z)</option>
          <option value="created_at">Date d'ajout</option>
        </select>
        <div className={`flex rounded-lg border overflow-hidden ${light ? 'border-gray-200' : 'border-white/10'}`}>
          <button onClick={() => setViewMode('list')} className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-[#E9641A] text-white' : light ? 'bg-white text-gray-400 hover:text-gray-700' : 'bg-white/5 text-white/30 hover:text-white'}`} data-testid="view-list-btn"><Layers size={14} /></button>
          <button onClick={() => setViewMode('grid')} className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-[#E9641A] text-white' : light ? 'bg-white text-gray-400 hover:text-gray-700' : 'bg-white/5 text-white/30 hover:text-white'}`} data-testid="view-grid-btn"><Film size={14} /></button>
        </div>
      </div>
      {showForm && <div className="mb-5"><ContentForm key={editContent?.id || 'new'} content={editContent} onSave={handleSave} onCancel={() => { setShowForm(false); setEditContent(null); fetch(); }} theme={theme} forcedType={fixedType} /></div>}
      {loading ? <LoadingSpinner /> : viewMode === 'list' ? (
        <div className="space-y-1">
          {sortedContents.map(item => (
            <div key={item.id} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${light ? 'bg-white border-gray-200 shadow-sm hover:border-gray-300' : 'bg-[#1a0e2e] border-white/5 hover:border-white/10'}`}>
              <img src={item.thumbnail_url || item.poster_url} alt="" className="w-12 h-16 object-cover rounded flex-none" />
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold truncate ${light ? 'text-gray-800' : 'text-white'}`}>{item.title}</p>
                <p className={`text-[10px] capitalize ${light ? 'text-gray-400' : 'text-white/35'}`}>{item.type} · {(item.categories || [item.category]).join(', ')} · {item.year} <FilterBadge item={item} className="ml-1 normal-case" /></p>
              </div>
              <div className={`flex items-center gap-1.5 text-[10px] flex-none ${light ? 'text-gray-400' : 'text-white/30'}`}>
                <Eye size={10} />{(item.views || 0).toLocaleString()}
                {ratingStats[item.id] && <span className="text-pink-400">♥ {likeScore(item)}</span>}
                <span className={item.status === 'draft' ? 'text-yellow-500' : 'text-green-400'}>{item.status === 'draft' ? 'Brouillon' : 'Publié'}</span>
              </div>
              <div className="flex items-center gap-0.5 flex-none">
                <button onClick={() => { setEditContent(item); setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }} data-testid={`content-edit-${item.id}`} className={`p-1.5 rounded transition-colors ${light ? 'text-gray-300 hover:text-gray-700 hover:bg-gray-100' : 'text-white/25 hover:text-white hover:bg-white/10'}`}><Edit size={13} /></button>
                <button onClick={() => handleDelete(item.id)} data-testid={`content-delete-${item.id}`} className={`p-1.5 rounded transition-colors ${light ? 'text-gray-300 hover:text-red-500 hover:bg-red-50' : 'text-white/25 hover:text-red-400 hover:bg-red-400/10'}`}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {sortedContents.map(item => (
            <div key={item.id} className={`group rounded-xl border overflow-hidden transition-all hover:scale-[1.02] ${light ? 'bg-white border-gray-200 shadow-sm hover:shadow-md' : 'bg-[#1a0e2e] border-white/5 hover:border-white/15'}`}>
              <div className="relative aspect-[2/3]">
                {(item.poster_url || item.thumbnail_url) ? <img src={item.poster_url || item.thumbnail_url} alt={item.title} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-white/5" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => { setEditContent(item); setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }} data-testid={`content-edit-grid-${item.id}`} className="p-1.5 rounded-lg bg-black/60 text-white/80 hover:text-white hover:bg-black/80 transition-colors"><Edit size={12} /></button>
                  <button onClick={() => handleDelete(item.id)} data-testid={`content-delete-${item.id}`} className="p-1.5 rounded-lg bg-black/60 text-red-400 hover:text-red-300 hover:bg-black/80 transition-colors"><Trash2 size={12} /></button>
                </div>
                <span className={`absolute top-2 left-2 px-1.5 py-0.5 rounded text-[9px] font-bold ${item.status === 'draft' ? 'bg-yellow-500/90 text-black' : 'bg-green-500/90 text-white'}`}>{item.status === 'draft' ? 'Brouillon' : 'Publié'}</span>
                <FilterBadge item={item} className="absolute bottom-2 left-2" />
                <MissingMediaBadge item={item} className="absolute bottom-2 right-2" />
              </div>
              <div className="p-2.5">
                <p className={`text-xs font-semibold truncate ${light ? 'text-gray-800' : 'text-white'}`}>{item.title}</p>
                <p className={`text-[10px] capitalize truncate ${light ? 'text-gray-400' : 'text-white/35'}`}>{item.type} · {item.year} · <Eye size={9} className="inline" /> {(item.views || 0).toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── CATEGORIES TAB ─── */

